import { useEffect, useRef, useCallback } from 'react';

/**
 * Prevents Carbon Popovers from triggering unintended row expansions when closed.
 *
 * Carbon forces focus back to the trigger button when a popover closes, which causes
 * interactive table rows to accidentally expand/collapse. This hook safely neutralizes
 * that auto-focus behavior and handles outside clicks manually.
 *
 */
export const usePopoverRowShield = () => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // The Programmatic Click-Outside Logic
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const button = triggerRef.current;
      if (!button) {
        return;
      }

      const isMenuOpen = button.getAttribute('aria-expanded') === 'true';
      if (!isMenuOpen) {
        return;
      }

      const target = e.target as Element;

      if (button.contains(target) || target.closest('#popover-menu')) {
        return;
      }

      button.click();
    };

    document.addEventListener('mousedown', handleOutsideClick, true);
    return () => document.removeEventListener('mousedown', handleOutsideClick, true);
  }, []);

  const attachShieldRef = useCallback((carbonRef: unknown) => {
    return (node: HTMLButtonElement | null) => {
      if (node) {
        // Neutralize Carbon's auto-focus to prevent row expansion
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        node.focus = () => {};
      }

      triggerRef.current = node;

      if (!carbonRef) {
        return;
      }
      if (typeof carbonRef === 'function') {
        carbonRef(node);
      } else {
        (carbonRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      }
    };
  }, []);

  return attachShieldRef;
};
